import React from "react";
import {
  MapmakerBusinessId,
  MapmakerDesign,
  OpeningFeature,
  OpeningInput,
  RectangularPrintLayoutInputWithFiles,
} from "@mapmaker/core";
import OpeningGroup from "./OpeningGroup";
import OpeningImages from "./OpeningImages";
import SvgNode from "../SvgNode";
import {
  MapmakerSvgContextProvider,
  MapmakerSvgContextValue,
} from "../MapmakerSvgContext";
import { getLayerByPriority } from "./openingUtils";

/*
 * Renders an SVG which is suitable for printing a photo which has 1 or more images that when cut
 * out will fit into the opening of a design.
 */
export interface IOpeningsStickerSheetStickersSvgProps {
  design: Pick<MapmakerDesign, "features" | "businessId">;
  printLayout: RectangularPrintLayoutInputWithFiles;
  width: number;
  height: number;
  onImageLoadError?: MapmakerSvgContextValue["onImageLoadError"];
}

export default function OpeningsStickerSheetStickersSvg({
  design,
  printLayout,
  width,
  height,
  onImageLoadError,
}: IOpeningsStickerSheetStickersSvgProps) {
  // Always use 300 DPI images for sticker sheets.
  const svgScale = Math.max(
    ((width / printLayout.width) * 300) / 72,
    ((height / printLayout.height) * 300) / 72
  );

  return (
    <svg
      id="print-view-svg"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={`0 0 ${printLayout.width} ${printLayout.height}`}
      width={width}
      height={height}
    >
      <MapmakerSvgContextProvider
        value={{
          svgScale,
          onImageLoadError,
          businessId: design.businessId as MapmakerBusinessId,
        }}
      >
        {printLayout.printOpenings.map((printOpening, i) => {
          if (!printOpening.file) {
            throw new Error(
              "Nested opening does not contain file data, so we cannot create the sticker."
            );
          }
          const opening = design.features[
            printOpening.openingId
          ] as OpeningFeature;
          const openingInput = printOpening.file.inputs[
            printOpening.openingId
          ] as OpeningInput;

          const cutLayer = getLayerByPriority(
            opening,
            design.businessId === "tbl" && !opening.outlined
              ? ["print", "cut"]
              : ["cut", "print"]
          );

          return (
            <OpeningGroup
              key={`${opening.id}-${i}`}
              opening={opening}
              layer={opening.outlined ? "cut" : "print"}
              x={printOpening.x}
              y={printOpening.y}
              rotation={printOpening.rotation}
              scale={printOpening.scale}
            >
              {openingInput.background ? (
                <SvgNode
                  nodeData={opening.layout.print}
                  fill={openingInput.background}
                />
              ) : null}
              <OpeningImages
                opening={opening}
                openingInput={openingInput}
                clipLayer="print"
              />
              {/* This path gives us our sticker cutter data */}
              <SvgNode
                nodeData={cutLayer}
                stroke={"red"}
                strokeWidth={1}
                fill="none"
              />
            </OpeningGroup>
          );
        })}
      </MapmakerSvgContextProvider>
    </svg>
  );
}
